import _ from 'lodash';
import clusterIcon from 'Images/cluster/m1.png';
import * as placeholderImgs from '../dashboard/img';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import OverlappingMarkerSpiderfier from 'overlapping-marker-spiderfier';
import { isValidCoords } from 'Utils/coordinates';

const EXCLUDED_EQ_TYPES = new Set([
  'alarms',
  'earthquakes',
  'gps',
  'lpr',
  'shutoff',
]);
  /**
   * @param {object|string} eq An equipment or its type
   * @returns {boolean}
   */
function eqIsMappable(eq) {
  return !EXCLUDED_EQ_TYPES.has(eq.equipmentType || eq);
}

/* @ngInject */
export default function MapService($http, $interval, MatchMapStyles, EquipmentInfoService) {
  const service = this;
  const apiKey = 'AIzaSyA2DcnENs_cHfHHIrRhr1ySutXObSG3-NY';
  const symEquipment = Symbol.for('equipment');

  const mapCache = {};

  const defaultVerticalOffset = -30;

  service.arrowSymbol = {
    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
    strokeOpacity: 1,
    scale: 2
  };
  service.breadcrumbSymbol = {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 8,
    strokeWeight: 2,
    strokeColor: '#000000',
    fillColor: null,
    fillOpacity: 1
  };

  service.MARKER_ICON_WIDTH = 25;
  service.MARKER_ICON_HEIGHT = 33;
  service.MARKER_ANCHOR_LAT = service.MARKER_ICON_WIDTH / 2;
  service.MARKER_ANCHOR_LNG = service.MARKER_ICON_HEIGHT;

  service.Map = Map;
  service.Marker = Marker;
  service.MarkerCluster = MarkerCluster;
  service.MarkerSpiderfier = MarkerSpiderfier;
  service.Polygon = Polygon;
  service.DrawingManager = DrawingManager;
  service.Infobox = Infobox;
  service.ContentBox = ContentBox;
  service.CameraContentBox = CameraContentBox;
  service.GeocodeContentBox = GeocodeContentBox;
  service.Polyline = Polyline;

  service.formatEventDate = formatEventDate;
  service.eqIsMappable = eqIsMappable;
  service.waitForTilesLoaded = waitForTilesLoaded;
  service.appendPinNotificationsControls = appendPinNotificationsControls;
  service.fitZoomMap = fitZoomMap;
  service.zoom = zoom;
  service.changeCenter = changeCenter;
  service.safeResize = safeResize;
  service.geoCode = geoCode;
  service.removeIntervalsAndCloseInfoWins = removeIntervalsAndCloseInfoWins;
  service.destroyGoogleMaps = destroyGoogleMaps;
  service.unsetMarkers = unsetMarkers;
  service.getBoundsZoomLevel = getBoundsZoomLevel;


  // ///// Shim addEventListener to capture and store registered event listeners. ///// //
  const correctProto = EventTarget.prototype ? 'prototype' : '__proto__';

  const addEventListener = EventTarget[correctProto].addEventListener;
  // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener
  EventTarget[correctProto].addEventListener = function (eventName, listener, optionsOrUseCapture, wantsUntrusted) {
    _.update(this, ['__listeners_', eventName], (listeners = []) => listeners.push(listener) && listeners);
    if (_.isNil(optionsOrUseCapture) || _.isObject(optionsOrUseCapture)) {
      // ? Any touch or scroll events should be passive in order to improve scrolling performance
      // * Note that our mouse/wheel events here use preventDefault and so cannot be passive
      const passive = (/touch(start|end|move)/).test(eventName);
      optionsOrUseCapture = _.assign(optionsOrUseCapture, { passive });
    }
    return addEventListener.apply(this, [eventName, listener, optionsOrUseCapture, wantsUntrusted]);
  };

  const removeEventListener = EventTarget[correctProto].removeEventListener;
  EventTarget[correctProto].removeEventListener = function (eventName, listener, ...args) {
    if (_.has(this, ['__listeners_', eventName])) {
      _.pull(this.__listeners_[eventName], listener);
    }
    return removeEventListener.apply(this, [eventName, listener, ...args]);
  };

  google.maps.Polygon.prototype.getBounds = function () {
    const bounds = new google.maps.LatLngBounds();
    this.getPaths().forEach(path => path.forEach(point => bounds.extend(point)));
    return bounds;
  };
  // ///// End Shim ///// //

  /**
   * @param {object} element the html DOM node to provide to Google maps constructor
   * @param {string} id the unique identifier to label maps
   * @param {object} [options={}] any extra key/value pairs to assign to the map
   *
   * @returns {object} a Google map
   */
  function Map(element, id, options = {}) {
    options = {
      mapTypeId: google.maps.MapTypeId.HYBRID,
      gestureHandling: 'cooperative',
      mapTypeControl: false,
      rotateControl: false,
      tilt: 0,
      streetViewControl: false,
      styles: MatchMapStyles,
      ...options
    };

    let map;
    if (mapCache[id]) {
      map = mapCache[id];
      map.premade = true;
      // ? Options in cache can often differ from requested options
      map.setOptions(options);
      element.parentNode.replaceChild(map.getDiv(), element);
    } else {
      map = new google.maps.Map(element, options);
      mapCache[id] = map;
    }
    return map;
  }

  /**
   * @param {object} [map] a Google map
   * @param {object} [param1] an equipment
   * @param {object} [param1.settings] container housing the latitude and longitude
   * @param {string} [param1.name] the equipment's name
   * @param {string} [param1.id] the equipment's identifier
   * @param {object} [image] options used to construct Icon object
   * @param {boolean} [slidingMarker=false] whether or not to construct a slidingMarker instead of a regular marker
   * @param {object} [options={}] extra key/value pairs to assign to the marker
   *
   * @returns {object} a Google marker or slidingMarker
   */
  function Marker(
    map,
    { settings = {}, name: title, id } = {},
    image,
    slidingMarker = false,
    options = {}
  ) {

    let position = options?.position;

    if (!position) {
      settings.latitude = _.isString(settings.latitude) ? parseFloat(settings.latitude) : settings.latitude;
      settings.longitude = _.isString(settings.longitude) ? parseFloat(settings.longitude) : settings.longitude;

      if (!isValidCoords(settings.latitude, settings.longitude)) {
        console.error('Invalid lat/lng on equipment with id', id, settings?.latitude, settings?.longitude);
      }

      position = new google.maps.LatLng(
        settings.latitude,
        settings.longitude
      );
    }

    const icon = image ? new Icon(image) : null;

    const marker = slidingMarker ?
      new SlidingMarker(_.assign({
        position,
        title,
        settings,
        id,
        map,
        icon,
        zIndex: 0,
        easing: 'linear'
      }, options)) :
      new google.maps.Marker(_.assign({
        map,
        position,
        icon,
        id,
        title
      }, options));

    return marker;
  }

  /**
   * Shared by the infobox and the marker hover tooltip so the two cannot drift
   * apart.
   *
   * @param {string|number|Date} eventDate the event's date
   *
   * @returns {object} {date, time} localised strings
   */
  function formatEventDateParts(eventDate) {
    const parsed = new Date(eventDate);
    return {
      date: parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      time: parsed.toLocaleTimeString('en-US', {
        timeZoneName: 'short',
      }),
    };
  }

  /**
   * @param {string|number|Date} eventDate the event's date
   *
   * @returns {string} a single-line date and time, for hover tooltips
   */
  function formatEventDate(eventDate) {
    if (!eventDate) {
      return 'no date';
    }
    const { date, time } = formatEventDateParts(eventDate);
    return `${date} ${time}`;
  }

  /**
   * @param {object} image options
   *
   * @returns {object} object to be used by Google Maps Icon constructor
   */
  function Icon(image) {
    const iconProp = ['size', 'origin', 'anchor'];
    return _.defaults(
      _.reduce(iconProp, (memo, prop) => {
        if (image[prop]) {
          memo[prop] = new google.maps[prop === 'size' ? 'Size' : 'Point'](image[prop].lat, image[prop].lng);
        }
        return memo;
      }, {}),
      image
    );
  }

  /**
   * @param {object} map The Google map to set to the new cluster
   * @param {object} options custom parameters for the markerclusterer
   *
   * @returns {object} a MarkerCluster object https://github.com/googlemaps/js-marker-clusterer
   */
  function MarkerCluster(map, options) {
    return new MarkerClusterer(map, [], _.assign({
      maxZoom: 16,
      ignoreHidden: true,
      styles: [
        MarkerClusterer.withDefaultStyle({
          textColor: 'black',
          url: clusterIcon,
          height: 55,
          width: 56,
        })
      ]
    }, options));
  }

  function MarkerSpiderfier(map, options = {}) {
    return new OverlappingMarkerSpiderfier(map, {
      markersWontMove: true,
      markersWontHide: false,
      circleSpiralSwitchover: 6,
      nearbyDistance: 35,
      keepSpiderfied: true,
      ...options
    });
  }

  /**
   * This is for creating polygons.  Polygons are shapes that can be overlayed on the map.
   *
   * @param {object} [map] a Google map that the polygon lives on.  Can also add polygons to maps with setMap()
   * @param {Array<object>} [coords] An array of coordinates objects representing verticies of the polygon
   * @param {boolean} [editable] Determines if the polygon can be edited from the map or not
   * @param {object} [options={}] extra key/value pairs to assign to the polygon see:
   * https://developers.google.com/maps/documentation/javascript/reference/polygon#Polygon
   * @returns {object} a Google polygon shape
   */
  function Polygon(
    map,
    coords = [],
    editable = false,
    options = {
      fillColor: '#046bb5',
      fillOpacity: 0.3,
      strokeColor: '#F0AD4E',
      strokeWeight: 5,
      zIndex: 1
    }
  ) {
    const polygon = new google.maps.Polygon(_.assign({
      map,
      path: coords,
      editable
    }, options));

    return polygon;
  }

  /**
   * Drawing Manager is a series of buttons in google maps that populate and draw shapes to edit or drag the map
   *
   * @param {object} [map] a Google map that the DrawingManager lives on.  Can also add DM to maps with setMap()
   * @param {object} [options={}] Styling and control options for the polygon that DM draws
   * @param {object} [drawingMode] defines the type of overlay (shapes or null) to be added on the map
   * @param {object} [drawingControlOptions] The display options for the drawing control. position and drawingModes
   * @param {object} [polygonOptions] extra key/value pairs to assign to the DM see:
   * https://developers.google.com/maps/documentation/javascript/reference/drawing
   * @returns {object} a Google DrawingManager
   */
  function DrawingManager(
    map, // This may need to be defaulted to null
    options = {},
    drawingMode = google.maps.drawing.OverlayType.POLYGON,
    drawingControlOptions = {
      position: google.maps.ControlPosition.TOP_CENTER,
      drawingModes: ['polygon']
    },
    polygonOptions = {
      fillColor: '#046bb5',
      fillOpacity: 0.3,
      clickable: false,
      editable: true,
      strokeColor: '#F0AD4E',
      strokeWeight: 5,
      zIndex: 1
    }
  ) {
    const drawingManager = new google.maps.drawing.DrawingManager(_.assign({
      map,
      drawingMode,
      drawingControlOptions,
      polygonOptions
    }, options));

    return drawingManager;
  }

  /**
   * @typedef {object} LatLng
   * @property {number} lat
   * @property {number} lng
   * {@link https://developers.google.com/maps/documentation/javascript/reference/coordinates#LatLngLiteral}
   */

  /**
   * @typedef {object} Size
   * @property {number} height
   * @property {number} width
   * {@link https://developers.google.com/maps/documentation/javascript/reference/coordinates#Size}
   */

  /**
   * @typedef {object} InfoBoxOptions
   * @property {string|Node} content The content of the InfoBox (plain text or an HTML DOM node).
   * @property {boolean} [disableAutoPan=false] Disable auto-pan on <tt>open</tt>.
   * @property {number} maxWidth The maximum width (in pixels) of the InfoBox. Set to 0 if no maximum.
   * @property {Size} pixelOffset The offset (in pixels) from the top left corner of the InfoBox
   * (or the bottom left corner if the <code>alignBottom</code> property is <code>true</code>)
   * to the map pixel corresponding to <tt>position</tt>.
   * @property {LatLng} position The geographic location at which to display the InfoBox.
   * @property {number} zIndex The CSS z-index style value for the InfoBox.
   * Note: This value overrides a zIndex setting specified in the <tt>boxStyle</tt> property.
   * @property {string} [boxClass="infoBox"] The name of the CSS class defining the styles for the InfoBox container.
   * @property {object} [boxStyle] An object literal whose properties define specific CSS
   * style values to be applied to the InfoBox. Style values defined here override those that may
   * be defined in the <code>boxClass</code> style sheet. If this property is changed after the
   * InfoBox has been created, all previously set styles (except those defined in the style sheet)
   * are removed from the InfoBox before the new style values are applied.
   * @property {string} closeBoxMargin The CSS margin style value for the close box.
   * The default is "2px" (a 2-pixel margin on all sides).
   * @property {string} closeBoxTitle The tool tip for the close box. The default is " Close ".
   * @property {string} closeBoxURL The URL of the image representing the close box.
   * Note: The default is the URL for Google's standard close box.
   * Set this property to "" if no close box is required.
   * @property {Size} infoBoxClearance Minimum offset (in pixels) from the InfoBox to the
   * map edge after an auto-pan.
   * @property {boolean} [isHidden=false] Hide the InfoBox on <tt>open</tt>.
   * [Deprecated in favor of the <tt>visible</tt> property.]
   * @property {boolean} [visible=true] Show the InfoBox on <tt>open</tt>.
   * @property {boolean} alignBottom Align the bottom left corner of the InfoBox to the <code>position</code>
   * location (default is <tt>false</tt> which means that the top left corner of the InfoBox is aligned).
   * @property {string} pane The pane where the InfoBox is to appear (default is "floatPane").
   * Set the pane to "mapPane" if the InfoBox is being used as a map label.
   * Valid pane names are the property names for the <tt>google.maps.MapPanes</tt> object.
   * @property {boolean} enableEventPropagation Propagate mousedown, mousemove, mouseover, mouseout,
   * mouseup, click, dblclick, touchstart, touchend, touchmove, and contextmenu events in the InfoBox
   * (default is <tt>false</tt> to mimic the behavior of a <tt>google.maps.InfoWindow</tt>). Set
   * this property to <tt>true</tt> if the InfoBox is being used as a map label.
   */

  /**
   * @param {string} id unique identifier
   * @param {InfoBoxOptions} [options={}] extra pairs to assign to the infobox
   * @param {number} [verticalOffset=-30] integer provided to the pixelOffset, moving the infobox up or down
   *
   * @returns {object} an InfoBox object
   */
  function Infobox(id, options = {}, verticalOffset = defaultVerticalOffset) {
    const infoBoxSizeRatio = 0.7;
    const minimumInfoBoxSize = 280;
    const viewportWidth = window.innerWidth * infoBoxSizeRatio;
    const height = viewportWidth < minimumInfoBoxSize ? viewportWidth : minimumInfoBoxSize;

    const infobox = new InfoBox(_.merge({
      alignBottom: true,
      content: null,
      disableAutoPan: false,
      boxStyle: {
        maxWidth: '400px',
        width: '70%',
        height: `${height}px`,
        overflow: 'hidden',
        borderRadius: '3px'
      },
      maxWidth: 280,
      pixelOffset: new google.maps.Size(-height / 2, verticalOffset),
      zIndex: null,
      closeBoxMargin: '-9999999px -99999999px',
      closeBoxURL: '',
      isHidden: false,
      pane: 'floatPane',
      enableEventPropagation: true
    }, options));

    infobox.cameraId = id;
    infobox.id = id;

    return infobox;
  }

  /**
   * @param {string} html the contents to assign as the innerHTML
   * @param {string} [className=''] classes for the element
   *
   * @returns {HTMLElement} the div with assigned innerHTML and classes
   */
  function ContentBox(html, className = '') {
    const box = document.createElement('div');

    box.className = _.isArray(className) ? className.join(' ') : className;
    box.innerHTML = html;

    return box;
  }

  /**
   * @param {object} cam a camera equipment
   * @param {string} ctrl the controller name to reference
   * @param {string} [dash] name of the dashboard to link
   * @param {string} [className='panel-container'] class names to pass to ContentBox constructor
   *
   * @returns {object} ContentBox
   */
  function CameraContentBox(cam, ctrl, dash, className = 'panel-container') {
    const innerHTML =
        `<div class="panel no-bd  panel-stat">
          <div class="panel-heading">
            <h3 class="panel-title">${cam.name}</h3>
            ${dash ? `<i class="far fa-tachometer  header-icon view-icon"
                ng-click="${ctrl}.viewDashboard('${dash}')"
              >
              </i>` : ''}
            <i class="fas fa-times flat header-icon map_close"
               ng-click="${ctrl}.close(); $event.stopPropagation();"></i>
          </div>
          <div class="panel-body">
            ${cam.id ? `<rv-camera hide-controls="true"
                    settings-controls="true"
                    active-camera-id="'${cam.id}'"
                    active-camera-stream="1"
            >
            </rv-camera>` : '<div class="no-cam-on-rcu"><span>No camera available on this RCU</span></div>'}
          </div>
        </div>`;

    return new ContentBox(innerHTML, className);
  }

  /**
   * @param {object} marker a Google marker
   * @param {string} ctrl controller name to reference
   * @param {string} address physical address of coordinates
   * @param {string|number} lat latitude of equipment or event
   * @param {string|number} lng longitude of equipment or event
   * @param {boolean} [showRawCoordinates] whether or not to show the raw lat long values
   * @param {string} [className='panel-container'] classes to assign to the element
   *
   * @returns {object} ContentBox
   */
  function GeocodeContentBox(marker, ctrl, address, lat, lng, showRawCoordinates, className = 'panel-container') {
    const { title, eventDate, messagedetail, equipmentType, type } = marker;
    const equipment = marker[symEquipment];
    const { date, time } = formatEventDateParts(eventDate);
    const coords = `${_.round(Number(lat), 5)}, ${_.round(Number(lng), 5)}`;
    const innerHTML =
        `<div class="panel no-bd panel-stat">
          <div class="panel-heading">
            <h3 class="panel-title" title="${title}">${title}</h3>
            <i class="fas fa-times flat header-icon map_close"
              ng-click="${ctrl}.close(); $event.stopPropagation();"></i>
          </div>
          <div class="panel-body infobox-body">
            <span class="message-label">
                <img class="col-xs-4 image-equipment-type"
                    src="${placeholderImgs[equipment.equipmentType]}" />
                <p class="col-xs-8">${messagedetail || equipment.name}</p>
            </span>
            <div class="details">
              ${equipmentType ? `<span class="col-xs-12 equipmentType-label">
                  <p class="col-xs-12"><i class="fa fa-bolt"></i> ${equipmentType} - ${type}</p>
                </span>` : ''}

              ${eventDate ? `<span class="col-xs-12 eventDate-label">
                  <p class="col-xs-6"><i class="fa fa-calendar"></i> ${date}</p>
                  <p class="col-xs-6"><i class="fa fa-clock-o time-items"></i> ${time}</p>
                </span>` : ''}
              <span class="address-label details">
                 <p class="col-xs-12"><i class="fa fa-globe"></i> ${address}</p>
              </span>
              ${showRawCoordinates ? `<span class="latlng-label">
                <p class="col-xs-12">
                <i class="fa fa-location-arrow"></i> ${coords}
                <i class="fa fa-copy copy-latlng" title="Copy all details"
                  style="cursor:pointer;margin-left:6px;"
                  ng-click="${ctrl}.copyToClipboard($event); $event.stopPropagation();"></i></p>
              </span>` : ''}
            </div>
          </div>
        </div>`;

    return new ContentBox(innerHTML, className);
  }

  /**
   * @param {boolean} [straight] toggles different settings for the polyline
   * @param {object} path a new Google maps latLng object
   * @param {object} [map=null] a Google map
   *
   * @returns {object} a new Google Polyline
   */
  function Polyline(straight, path, map = null) {
    const polyline = {
      path,
      icons: straight ? [] : [{
        icon: angular.copy(service.arrowSymbol),
        offset: '0',
        repeat: '20px'
      }],
      strokeOpacity: straight ? 1 : 0,
      map
    };

    return new google.maps.Polyline(polyline);
  }

  /**
   * @param {object} map a Google map
   * @param {Function} cb
   * @param {number} [timeToWait=0] the millisecond wait time for the setTimeout
   * @returns {string|undefined}
   * If the map has been reused and thus owns the premade property, setTimeout will call the provided cb method
   * after a given amount of time. Otherwise, Google's addListenerOnce will call the cb method upon tilesloaded event
   */
  function waitForTilesLoaded(map, cb, timeToWait = 0) {
    return map.premade ? setTimeout(cb, timeToWait) : google.maps.event.addListenerOnce(map, 'tilesloaded', cb);
  }

  /**
   * Appends the "enable/disable Notifications" button to the top-right section of the provided map and attaches
   * listeners.
   *
   * @param {object} map a Google map
   * @param {string[]} equipmentIds list of ids to subscribe and unsubscribe
   *
   * @returns {undefined}
   */
  function appendPinNotificationsControls(map, equipmentIds) {
    map.controls[google.maps.ControlPosition.TOP_RIGHT].clear();
    const controlDiv = document.createElement('div');
    controlDiv.id = 'btn-map-pins';
    let subscribed = false;
    // Set CSS for the control border.
    const controlUI = document.createElement('div');
    controlUI.title = 'Click to toggle pin notifications';
    controlDiv.appendChild(controlUI);

    // Set CSS for the control interior.
    const controlText = document.createElement('div');
    controlText.className = 'btn btn-success';
    controlText.innerHTML = 'Enable Pin Notifications';
    controlUI.appendChild(controlText);

    controlDiv.index = 1;
    map.controls[google.maps.ControlPosition.TOP_RIGHT].push(controlDiv);

    // Setup the click event listeners: toggle pin notifications.
    controlUI.addEventListener('click', () => {
      if (subscribed) {
        subscribed = false;
        EquipmentInfoService.unsubscribeToGivenEquipment(equipmentIds);
        controlText.innerHTML = 'Enable Pin Notifications';
        controlText.className = 'btn btn-success';
      } else {
        subscribed = true;
        EquipmentInfoService.subscribeToGivenEquipment(equipmentIds);
        controlText.innerHTML = 'Disable Pin Notifications';
        controlText.className = 'btn btn-danger';
      }
    });
  }

  /**
   * @param {object} map a Google map
   * @param {Array<object>} markers list of Google markers
   * @param {number} [zoomLevel] amount to cool the map
   *
   * @returns {undefined}
   */
  function fitZoomMap(map, markers, zoomLevel) {
    const bounds = new google.maps.LatLngBounds();
    markers.forEach(marker => marker.getPosition && bounds.extend(marker.getPosition()));
    map.fitBounds(bounds);

    if (zoomLevel) {
      zoom(map, zoomLevel);
    }
  }

  /**
   * @param {object} map a Google map
   * @param {number} [zoomLevel=3] amount to zoom
   *
   * @returns {undefined}
   */
  function zoom(map, zoomLevel = 3) {
    map.setZoom(zoomLevel);
  }

  /**
   * @param {object} map a Google map
   * @param {object} coordinates container of latitude longitude values
   * @param {number} [zoomLevel] amount to zoom
   *
   * @returns {undefined}
   */
  function changeCenter(map, coordinates, zoomLevel) {
    let lat;
    let lng;
    if (coordinates.latitude) {
      lat = Number(coordinates.latitude);
      lng = Number(coordinates.longitude);
    } else {
      [lat, lng] = calculatePointBetweenTwoMarkers(coordinates.start, coordinates.end);
    }
    map.setCenter({ lat, lng });

    if (zoomLevel) {
      zoom(map, zoomLevel);
    }
  }

  /**
   * Gives a bit of extra time before triggering the resize event to ensure that it isn't called in the middle of
   * panel transformation.
   *
   * @param {object} map a Google map
   * @param {number} [timeToWait=100] millisecond wait time
   *
   * @returns {undefined}
   */
  function safeResize(map, timeToWait = 100) {
    setTimeout(() => google.maps.event.trigger(map, 'resize'), timeToWait);
  }

  /**
   * Convert coordinates into address
   *
   * @param {number[]|string[]} args [lat, lng]
   * @returns {Promise<string>} The address
   */
  function geoCode([lat, lng]) {
    return $http.get(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`,
      { withCredentials: false }
    );
  }

  /**
   * @param {object | Array<object>} infoboxes the infobox/es to be closed
   */
  function removeIntervalsAndCloseInfoWins(infoboxes) {
    _.castArray(infoboxes).forEach((infowins) => {
      // TODO: does an infoBox ever have this property?? -Devin
      if (infowins.interval) {
        $interval.cancel(infowins.interval);
        infowins.interval = undefined;
      }
      infowins.close();
    });
  }

  /**
   * Detaches the Google map from the element of the provided selector and removes all event listeners cached
   * by the shimmed add and remove event listener methods so that garbage collection can destroy all DOM nodes.
   *
   * @param {string} elementSelector the selector used to locate the DOM element
   *
   * @returns {undefined}
   */
  function destroyGoogleMaps(elementSelector) {
    const elem = document.querySelector(elementSelector);
    elem.parentNode.removeChild(elem);

    removeAllGoogleListeners(window, 'blur');
    removeAllGoogleListeners(window, 'resize');
    [
      'click',
      'keydown',
      'keypress',
      'keyup',
      'MSFullscreenChange',
      'fullscreenchange',
      'mozfullscreenchange',
      'webkitfullscreenchange',
    ].forEach(ev => removeAllGoogleListeners(document, ev));
    // ASSUMPTION: No other library registers global resize and scroll event listeners!
    // If this is not true, then you'll need to add logic to avoid removing these.
    removeAllDOMListeners(window, 'resize');
    removeAllDOMListeners(window, 'scroll');
  }

  /**
   * @param {Array<object>} markers list of Google markers
   *
   * @returns {undefined}
   */
  function unsetMarkers(markers) {
    _.forEach(markers, marker => marker.setMap(null));
  }

  /**
   * Calculates the zoom level given the bounds of a polygon
   * Need to do some facny maths because google maps uses Mercator projection
   *
   * @param {object} bounds - latLngBounds instance:
   * https://developers.google.com/maps/documentation/javascript/reference/coordinates#LatLngBounds
   * @returns {number} Zoom level around polygon (21 being most zoomed)
   */
  function getBoundsZoomLevel(bounds) {
    const mapDim = { height: 400, width: 400 };
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    function latRad(lat) {
      const sin = Math.sin(lat * Math.PI / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function mercatorZoom(mapPx, worldPx, fraction) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = mercatorZoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = mercatorZoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  }

  /* ************** */
  /* HELPER METHODS */
  /* ************** */

  // Helper function: Removes all event listeners registered with Google's addDomListener function,
  // including from __e3_ properties on target objects.
  function removeAllGoogleListeners(target, event) {
    const listeners = target.__e3_;
    if (!listeners) {
      // Couldn't find property __e3_ containing Google Maps listeners. Perhaps Google updated the Maps SDK?;
      return;
    }
    const evListeners = listeners[event];
    if (evListeners) {
      _.forEach(_.keys(evListeners), (key) => {
        if (evListeners.hasOwnProperty(key)) {
          google.maps.event.removeListener(evListeners[key]);
        }
      });
    }
  }

  // Removes all DOM listeners for the given target and event.
  function removeAllDOMListeners(target, event) {
    const listeners = target.__listeners_;
    if (!listeners || !listeners[event]) {
      return;
    }
    // Copy to avoid iterating over array that we mutate via removeEventListener
    const listenerCopy = [...listeners[event]];
    _.forEach(listenerCopy, listener => target.removeEventListener(event, listener));
  }

  function calculatePointBetweenTwoMarkers(coordA, coordB) {
    const pointA = {
      lat: coordA.lat,
      lng: coordA.lon || coordA.lng
    };
    const pointB = {
      lat: coordB.lat,
      lng: coordB.lon || coordB.lng
    };
    const center = new google.maps.LatLngBounds(pointA, pointB).getCenter();
    return [center.lat(), center.lng()];
  }
}
